import {Flag, type ColorSchemeType} from 'platform/components';
import {Box, Grid, GridItem, HStack, Link, Text} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {BatchVehiclePricingStatus, VehicleItem} from '../../../types/api/api';
import {importPricingGrid} from '../constants/importPricingGrid';
import {ImportVehicle} from '../types/ImportVehicle';
import {getBatchVehiclePriceReportHref} from '../utils/getBatchVehiclePriceReportHref';
import {getBatchVehicleRecommendedPrice} from '../utils/getBatchVehicleRecommendedPrice';
import {pricingStatusToRowStatus} from '../utils/pricingStatusToRowStatus';
import {
  ImportVehicleListRowFrame,
  ImportVehicleListRowIndicator,
} from './ImportVehicleListRowFrame';
import {ImportVehicleSummary} from './ImportVehicleSummary/ImportVehicleSummary';

interface VehiclePricingResultRowProps extends TestIdProps {
  vehicleItem: VehicleItem;
  importVehicle: ImportVehicle;
}

function pricingStatusLabel(status: BatchVehiclePricingStatus): string {
  return match<BatchVehiclePricingStatus, string>(status)
    .with('priced', () => i18n.t('page.import.pricingStatus.priced'))
    .with('failed', () => i18n.t('page.import.pricingStatus.failed'))
    .with('pending', () => i18n.t('page.import.pricingStatus.pending'))
    .exhaustive();
}

function pricingStatusColorScheme(status: BatchVehiclePricingStatus): ColorSchemeType {
  return match<BatchVehiclePricingStatus, ColorSchemeType>(status)
    .with('priced', () => 'green')
    .with('failed', () => 'red')
    .with('pending', () => 'orange')
    .exhaustive();
}

export function VehiclePricingResultRow(props: VehiclePricingResultRowProps) {
  const formatCurrency = useFormatCurrency();
  const recommended = getBatchVehicleRecommendedPrice(props.vehicleItem);
  const reportHref = getBatchVehiclePriceReportHref(props.vehicleItem);
  const pricingRowStatus = pricingStatusToRowStatus(props.vehicleItem.pricingStatus);

  return (
    <ImportVehicleListRowFrame
      $rowStatus={pricingRowStatus}
      data-testid={suffixTestId('row', props)}
    >
      <ImportVehicleListRowIndicator $rowStatus={pricingRowStatus} />
      <Box paddingLeft={4} paddingRight={4} paddingTop={2} paddingBottom={2}>
        <Grid
          columns={importPricingGrid.columns}
          horizontalSpacing={4}
          verticalSpacing={0}
          align="center"
        >
          <GridItem span={importPricingGrid.specificationSpan}>
            <ImportVehicleSummary
              vehicle={props.importVehicle}
              data-testid={suffixTestId('vehicleSummary', props)}
            />
          </GridItem>
          <GridItem span={importPricingGrid.cellSpan}>
            <HStack justify="flex-end" width="100%">
              <Flag
                label={pricingStatusLabel(props.vehicleItem.pricingStatus)}
                colorScheme={pricingStatusColorScheme(props.vehicleItem.pricingStatus)}
                isSubtle
                data-testid={suffixTestId('pricingStatusFlag', props)}
              />
            </HStack>
          </GridItem>
          <GridItem span={importPricingGrid.cellSpan}>
            {isNotNil(recommended) ? (
              <Text size="base" color="primary" align="right">
                {formatCurrency(recommended.amount, recommended.currency) ?? EMPTY_PLACEHOLDER}
              </Text>
            ) : (
              <Text size="base" color="secondary" align="right">
                {i18n.t('page.import.pricing.priceUnavailable')}
              </Text>
            )}
          </GridItem>
          <GridItem span={importPricingGrid.cellSpan}>
            {isNotNil(reportHref) ? (
              <HStack justify="flex-end" width="100%">
                <Link
                  href={reportHref}
                  target="_blank"
                  rel="noreferrer"
                  leftIcon="action/open_in_new"
                  ariaLabel={i18n.t('page.import.pricing.openPriceReport')}
                  size="small"
                  data-testid={suffixTestId('priceReportLink', props)}
                />
              </HStack>
            ) : null}
          </GridItem>
        </Grid>
      </Box>
    </ImportVehicleListRowFrame>
  );
}
